<template>
    <section class="hero section">
        <SkewedImage
            :src="require('~/assets/images/rent-out-hero-bg.webp')"
            :src-small="require('~/assets/images/rent-out-hero-bg-small.webp')"
            background-position="center"
            :preload-images="true"
        />
        <div class="hero__content-wrapper">
            <div class="hero__content">
                <h1 class="hero__heading">
                    <template v-if="isLargeScreen">
                        {{ $t('sections.rent_out_hero.heading') }}
                    </template>
                    <template v-else>
                        {{ $t('sections.rent_out_hero.mobile_heading') }}
                    </template>
                </h1>
                <div class="hero__description">
                    <img
                        width="35"
                        height="39"
                        :alt="$t('icon_img_alt.insurance')"
                        src="~/assets/images/big-icons/insurance.svg"
                    />
                    <div>
                        <span>{{ $t('sections.rent_out_hero.description') }}</span>
                        <LocalizedLink :to="{ name: 'insurance' }">
                            {{ $t('common.read_more') }}
                        </LocalizedLink>
                    </div>
                </div>
                <LocalizedLink
                    v-if="isLoggedIn && isOwner"
                    :to="{ name: 'owner-publish-vehicle' }"
                    class="hero__button button button--primary button--full-width"
                >
                    {{ $t('sections.rent_out_hero.list_your_camper') }}
                </LocalizedLink>
                <ModalLink
                    v-if="!isLoggedIn"
                    to="sign-up"
                    :data="{ role: 'owner' }"
                    class="hero__button button button--primary button--full-width"
                >
                    {{ $t('sections.rent_out_hero.list_your_camper') }}
                </ModalLink>
                <a
                    href="#price-calculator"
                    class="hero__button button button--secondary-contrast button--full-width"
                    @click="scrollToPriceCalculator"
                >
                    {{ $t('sections.rent_out_hero.calculate_earnings') }}
                </a>
                <div class="hero__help">
                    <div class="hero__help__item">
                        <svg-icon name="request_call" />
                        <ModalLink to="support-request">
                            {{ $t('sections.rent_out_hero.request_a_call') }}
                        </ModalLink>
                    </div>
                </div>
            </div>
            <div class="hero__bottom">
                <div class="hero__earnings-container">
                    <div class="hero__earnings-bg" />
                    <i18n tag="div" class="hero__earnings" path="sections.rent_out_hero.earn_up_to">
                        <template #price>
                            <div class="hero__earnings__price">
                                {{ formattedEarnings }}
                            </div>
                        </template>
                        <template #perWeek>
                            <div class="hero__earnings__per-week">{{ $t('common.per_week') }}</div>
                        </template>
                    </i18n>
                </div>
                <div class="hero__ratings-container">
                    <div class="hero__ratings-bg" />
                    <PlatformRatings class="hero__ratings" />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { mapGetters } from 'vuex';
import ModalLink from '~/components/modals/ModalLink.vue';
import PlatformRatings from '~/components/PlatformRatings.vue';
import LocalizedLink from '~/components/LocalizedLink.vue';
import SkewedImage from '~/components/SkewedImage.vue';

export default {
    components: {
        ModalLink,
        PlatformRatings,
        LocalizedLink,
        SkewedImage,
    },
    computed: {
        ...mapGetters(['isLoggedIn', 'isLargeScreen', 'isOwner', 'userCurrency', 'siteCountryCode']),
        formattedEarnings() {
            switch (this.siteCountryCode) {
                case 'CH':
                    return this.$t('sections.rent_out_hero.earnings_value_ch');
                case 'DE':
                    return this.$t('sections.rent_out_hero.earnings_value_de');
                case 'AT':
                    return this.$t('sections.rent_out_hero.earnings_value_at');
                default:
                    return this.$t('sections.rent_out_hero.earnings_value', {
                        currency: this.userCurrency.toUpperCase(),
                    });
            }
        },
    },
    methods: {
        scrollToPriceCalculator(event) {
            if (event.metaKey || event.shiftKey || event.ctrlKey) {
                return;
            }

            const element = document.getElementById('price-calculator');

            if (!element) {
                return;
            }

            event.preventDefault();
            window.scrollTo({
                top: element.offsetTop,
                left: 0,
                behavior: 'smooth',
            });
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';
$earnings-border-radius: 20px;

.hero {
    text-align: left;
    @include font-size(16px);

    &.section {
        position: relative;
        padding: 0 10px;

        @include md {
            padding: 0;
            margin: 0 $lg-spacing;
        }
    }

    &__description {
        display: flex;
        align-items: center;
        margin-bottom: 24px;
        @include font-size(14px);
        line-height: 20px;
        font-weight: bold;

        img {
            margin-right: 8px;
        }

        a {
            color: $primary;
            font-weight: bold;
        }

        @include lg {
            @include font-size(20px);
            line-height: 24px;
        }
    }

    &__content-wrapper {
        padding: 24px 0;
        max-width: 400px;

        @include md {
            margin: 0 110px 0 80px;
        }

        @include lg {
            display: flex;
            justify-content: space-between;
            max-width: unset;
            padding: 70px 0 32px;
        }
    }

    &__content {
        @extend %card;
        border-radius: 20px;
        padding: 32px 24px 24px;
        box-sizing: border-box;
        max-width: 400px;
        position: relative;

        @include lg {
            padding: 56px 40px 40px;
            width: auto;
        }

        @include xl {
            max-width: 465px;
        }
    }

    &__heading {
        @include font-size(28px);
        line-height: 32px;
        text-transform: none;
        margin-bottom: 24px;
        font-weight: 600;
        z-index: 10;

        @include lg {
            @include font-size(40px);
            line-height: 42px;
        }
    }

    &__button {
        &.button--primary {
            margin-bottom: 8px;
        }
    }

    &__help {
        margin-top: 24px;

        &__item {
            display: flex;

            &:not(:last-child) {
                margin-bottom: 16px;
            }

            p {
                margin-bottom: 8px;
            }

            span {
                @include font-size(14px);
            }

            .svg-icon {
                width: 24px;
                height: 24px;
                min-width: 24px;
                margin-right: 8px;
            }

            a {
                color: $primary;
            }
        }
    }

    &__bottom {
        margin-top: 24px;

        @include lg {
            margin-top: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
    }

    &__ratings-container,
    &__earnings-container {
        position: relative;
    }

    &__earnings-container {
        width: fit-content;
        margin-bottom: 32px;
        overflow: hidden;
        border-radius: $earnings-border-radius;
    }

    &__earnings-bg,
    &__ratings-bg {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
    }

    &__ratings-bg {
        background: rgba(14, 24, 30, 0.5);
        filter: blur(16px);
    }

    &__ratings {
        position: relative;
        z-index: 2;

        @include lg {
            @include font-size(20px);
        }

        .ratings__icon.svg-icon {
            width: 32px;
            height: 32px;
        }
    }

    &__earnings-bg {
        $skewed-earnings-angle: 5deg;

        border-radius: $earnings-border-radius;
        -webkit-transform: skew($skewed-earnings-angle);
        -moz-transform: skew($skewed-earnings-angle);
        -o-transform: skew($skewed-earnings-angle);
        transform: skew($skewed-earnings-angle);
        background-color: $blue-light;
        width: calc(100% + #{$earnings-border-radius});
        margin-left: -23px;
    }

    &__earnings {
        position: relative;
        display: none;
        padding: 10px 32px 10px 16px;
        z-index: 2;
        font-weight: 600;

        &__price {
            @include font-size(42px);
            line-height: 42px;
        }

        &__per-week {
            @include font-size(14px);
        }

        @include lg {
            display: block;
        }
    }
}
</style>
